// DriverPayouts.js
import { useState, useEffect } from "react";
import {
  getDriverPayoutsList,
  getDriverPayoutsHistory,
  updateDriverPayoutStatus,
  exportDriverPayoutsCsv,
} from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import "./DriverPayouts.css";

// MUI Icons
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

const DriverPayouts = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [sortBy, setSortBy] = useState("fullName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [historySortBy, setHistorySortBy] = useState("createdAt");
  const [historySortOrder, setHistorySortOrder] = useState("desc");
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);
  const [selectedDriverName, setSelectedDriverName] = useState("");
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusError, setStatusError] = useState("");

  // Fetch drivers on mount
  const fetchDrivers = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getDriverPayoutsList({ page, pageSize: 10, sortBy, sortOrder });
      setDrivers(res.data || []);
      setPagination(res.pagination || { page, limit: 10, total: 0 });
    } catch (err) {
      console.error("Error fetching driver payouts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [sortBy, sortOrder]);

  // Open history modal
  const openHistory = async (driverId, driverName, page = 1, nextSortBy = historySortBy, nextSortOrder = historySortOrder) => {
    setSelectedDriverId(driverId);
    setSelectedDriverName(driverName);
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    try {
      const res = await getDriverPayoutsHistory({ driverId, page, pageSize: 10, sortBy: nextSortBy, sortOrder: nextSortOrder });
      setHistoryData(res.data || []);
      setHistoryPagination(res.pagination || { page, limit: 10, total: 0 });
    } catch (err) {
      console.error("Error fetching driver history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open status update modal
  const openStatusUpdate = (payoutId, driverName) => {
    setSelectedPayoutId(payoutId);
    setSelectedDriverName(driverName);
    setStatusModalOpen(true);
    setStatusError("");
  };

  // Handle status update
  const handleStatusUpdate = async (status) => {
    if (!selectedPayoutId) return;
    setStatusUpdateLoading(true);
    setStatusError("");
    try {
      await updateDriverPayoutStatus({ payoutId: selectedPayoutId, status });
      alert(`Payout status updated to ${status} successfully.`);
      // Refresh list and close modal
      fetchDrivers();
      setStatusModalOpen(false);
      setSelectedPayoutId(null);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Update failed.";
      setStatusError(msg);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Helper: format currency
  const formatCurrency = (val) => `$${val?.toFixed(2) || "0.00"}`;

  // Helper: status badge class
  const getStatusClass = (status) => {
    const map = {
      completed: "status-completed",
      failed: "status-failed",
      pending: "status-pending",
      instant: "status-instant",
    };
    return map[status?.toLowerCase()] || "";
  };

  // Helper: method type badge
  const getMethodClass = (method) => {
    const map = {
      instant: "method-instant",
      weekly: "method-weekly",
    };
    return map[method?.toLowerCase()] || "";
  };

  // Summary stats
  const totalDrivers = drivers.length;
  const totalOwed = drivers.reduce((sum, d) => sum + (d.owedAmount || 0), 0);
  const totalEarned = drivers.reduce((sum, d) => sum + (d.totalEarned || 0), 0);
  const totalCashedOut = drivers.reduce(
    (sum, d) => sum + (d.totalCashedOut || 0),
    0,
  );
  const driversWithOwed = drivers.filter((d) => d.owedAmount > 0).length;

  return (
    <div className="driver-payouts">
      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <>
          <ListControls sortOptions={[{value:"fullName",label:"Driver"},{value:"totalEarned",label:"Total earned"},{value:"owedAmount",label:"Owed amount"},{value:"totalCashedOut",label:"Cashed out"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
          <ExportCsvButton exportRequest={exportDriverPayoutsCsv} filters={{ view: "summary", sortBy, sortOrder }} filename="driver-payouts.csv" />
          <div className="table-wrapper">
            <table className="data_table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Email</th>
                  <th>Vehicle</th>
                  <th>Total Earned</th>
                  <th>Cashed Out</th>
                  <th>Owed Amount</th>
                  <th>Last Cash-Out</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{ textAlign: "center", padding: "30px 0" }}
                    >
                      No drivers found.
                    </td>
                  </tr>
                ) : (
                  drivers.map((driver) => (
                    <tr key={driver.id}>
                      <td>
                        <strong>{driver.fullName || "N/A"}</strong>
                      </td>
                      <td>{driver.email || "N/A"}</td>
                      <td>
                        {driver.vehicleType ? (
                          <span className="vehicle-badge">
                            <DirectionsCarIcon fontSize="small" />{" "}
                            {driver.vehicleType}
                          </span>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>{formatCurrency(driver.totalEarned)}</td>
                      <td>{formatCurrency(driver.totalCashedOut)}</td>
                      <td>
                        <strong className="owed-amount">
                          {formatCurrency(driver.owedAmount)}
                        </strong>
                      </td>
                      <td>
                        {driver.lastCashOut ? (
                          <div className="last-cashout">
                            <span className="cashout-amount">
                              {formatCurrency(driver.lastCashOut.amount)}
                            </span>
                            <span className="cashout-method">
                              {driver.lastCashOut.methodType || "N/A"}
                            </span>
                            <span className="cashout-date">
                              {new Date(
                                driver.lastCashOut.date,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        ) : (
                          "No cash-out"
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-outline"
                          onClick={() =>
                            openHistory(driver.id, driver.fullName)
                          }
                          title="View payout history"
                        >
                          <HistoryIcon fontSize="small" /> History
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination pagedata={pagination} onPageChange={fetchDrivers} />

          {/* ====== HISTORY MODAL ====== */}
          <Modal
            isOpen={historyModalOpen}
            onClose={() => setHistoryModalOpen(false)}
            title={`Payout History - ${selectedDriverName}`}
          >
            {historyLoading ? (
              <Loader />
            ) : (
              <div className="history-table-wrap">
                <ListControls sortOptions={[{value:"createdAt",label:"Created date"},{value:"amount",label:"Amount"},{value:"status",label:"Status"},{value:"methodType",label:"Method"},{value:"driver.fullName",label:"Driver"}]} sortBy={historySortBy} sortOrder={historySortOrder} onSortByChange={(value) => { setHistorySortBy(value); openHistory(selectedDriverId, selectedDriverName, 1, value, historySortOrder); }} onSortOrderChange={(value) => { setHistorySortOrder(value); openHistory(selectedDriverId, selectedDriverName, 1, historySortBy, value); }} />
                <table className="data_table">
                  <thead>
                    <tr>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Method</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          No payout history available.
                        </td>
                      </tr>
                    ) : (
                      historyData.map((item) => (
                        <tr key={item.id}>
                          <td>${item.amount}</td>
                          <td>
                            <span
                              className={`status-badge ${getStatusClass(item.status)}`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`method-badge ${getMethodClass(item.methodType)}`}
                            >
                              {item.methodType || "N/A"}
                            </span>
                          </td>
                          <td>
                            {item.payoutDate
                              ? new Date(item.payoutDate).toLocaleDateString()
                              : "Pending"}
                          </td>
                          <td>
                            {item.status?.toLowerCase() === "pending" && (
                              <button
                                className="btn-primary btn-small"
                                onClick={() => {
                                  setHistoryModalOpen(false);
                                  openStatusUpdate(item.id, selectedDriverName);
                                }}
                              >
                                <CheckCircleIcon fontSize="small" /> Complete
                              </button>
                            )}
                            {item.status?.toLowerCase() !== "pending" && (
                              <span className="no-action">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <Pagination pagedata={historyPagination} onPageChange={(page) => openHistory(selectedDriverId, selectedDriverName, page)} />
              </div>
            )}
          </Modal>

          {/* ====== STATUS UPDATE MODAL ====== */}
          <Modal
            isOpen={statusModalOpen}
            onClose={() => {
              setStatusModalOpen(false);
              setSelectedPayoutId(null);
              setStatusError("");
            }}
            title={`Update Payout Status - ${selectedDriverName}`}
          >
            <div className="status-update-form">
              <p>Please confirm that the payout has been completed.</p>
              {statusError && <p className="error-message">{statusError}</p>}
              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setStatusModalOpen(false);
                    setSelectedPayoutId(null);
                    setStatusError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => handleStatusUpdate("completed")}
                  disabled={statusUpdateLoading}
                >
                  {statusUpdateLoading ? "Updating..." : "Mark as Completed"}
                </button>
                <button
                  className="btn-danger"
                  onClick={() => handleStatusUpdate("failed")}
                  disabled={statusUpdateLoading}
                >
                  {statusUpdateLoading ? "Updating..." : "Mark as Failed"}
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default DriverPayouts;
